import React from 'react';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { Router } from 'react-router';
import { change } from 'redux-form';
import {
    syncHistoryWithStore,
    routerMiddleware,
    replace,
} from 'react-router-redux';
import { setLanguage } from '@eb/i18n';
import { configureStore } from '@eb/redux-configure-store';
import { getHistory, updateHistory } from '@eb/react-router-history';
import {
    middleware as analyticsMiddleware,
    GA_SETTINGS_PROP_TYPE,
} from '@eb/site-analytics';
import { HAS_WINDOW } from '@eb/feature-detection';
import { initializeRealUserMonitoring } from '@eb/real-user-monitoring';

import { isInitiatedByCheckout } from './common/utils';
import { rootReducer as reducer } from './common/reducers/root';
import getRoutes from './page/routes';
import {
    BASE_URL,
    CHECKOUT_PATH,
    LOOK_UP_FORM,
    SET_PASSWORD_FORM,
} from './common/constants';

const isProd = ({ env }) => {
    return env.ebDomain.indexOf('eventbrite') > -1;
};

/**** DO NOT COPY THIS PATTERN ****/
//This is needed so that if the user begins to type before the JS
//has finished loading we don't want the field to be cleared, instead
//we want the form to be initialized with that value so the user
//has a seamless experience, but is something we are going to be moving away from
//and will be refactored in the near future.
const _initializeFieldValues = (store) => {
    const emailField = document.getElementById('email');
    const passwordField = document.getElementById('set-password');
    let fieldValues = [];
    let form = LOOK_UP_FORM;

    if (emailField) {
        fieldValues = ['email', emailField.value];
    } else if (passwordField) {
        fieldValues = ['password', passwordField.value];
        form = SET_PASSWORD_FORM;
    }

    //Call change here since the onEnter hooks instantiate
    //the form and so to prevent overwriting the values we just
    //want to update the formField we care about.
    store.dispatch(change(form, ...fieldValues));
};

export default class AuthenticationApp extends React.Component {
    static propTypes = {
        gaSettings: GA_SETTINGS_PROP_TYPE,
    };

    constructor(props) {
        super(props);

        setLanguage(props.env.localeInfo.locale);

        const historyManagement = getHistory();
        const initialData = this._getInitialData();

        this._store = configureStore({
            reducer,
            middleware: [
                thunk,
                routerMiddleware(historyManagement),
                analyticsMiddleware(this.props),
            ],
            initialState: initialData,
        });
        this._history = syncHistoryWithStore(historyManagement, this._store);

        updateHistory(this._history, props.request);
    }

    componentDidMount() {
        const { isCheckoutLoginPath } = this.props;

        if (isProd(this.props)) {
            initializeRealUserMonitoring({
                sampleRate: 0.85,
                version: this.props.app_version,
                service: this.props.app_name,
            });
        }

        if (HAS_WINDOW && !isCheckoutLoginPath) {
            _initializeFieldValues(this._store);
        }
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        updateHistory(this._history, newProps.request);
    }

    _getInitialData() {
        const {
            request,
            env,
            user,
            fp,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            gaSettings,
            socialSSOSettings,
            featureFlags,
            userHasAccount,
            socialDataMock,
            skipSocialCalls,
        } = this.props;
        const isCheckoutRequest = isInitiatedByCheckout();

        return {
            gaSettings,
            isCheckoutRequest,
            socialDataMock: socialDataMock || {},

            authentication: {
                referrer: request.params.referrer || '',
                locale: env.localeInfo.locale,
                userTypeParams: {
                    userType: request.params['user_type'],
                    userTypeSig: request.params['user_type_sig'],
                },
                forward: request.params.forward || '',
                stld: request.params.stld || '',
                pckg: request.params.pckg || '',
                isAuthenticated: user.isAuthenticated,
                recaptcha2PublicKey,
                recaptcha3PublicKey,
                fp,
                disableRecaptcha3:
                    featureFlags && featureFlags.disableRecaptcha3
                        ? featureFlags.disableRecaptcha3
                        : false,
                featureFlags,
                userHasAccount,
            },
            frontend: {
                lateralImage: {},
            },
            social: {
                skipSocialCalls,
                socialSSOSettings,
            },
        };
    }

    render() {
        const { env, footerLinks, request, user, featureFlags } = this.props;
        const checkoutPath = `${BASE_URL}/${CHECKOUT_PATH}`;
        const isCheckoutLoginPath = request.path === checkoutPath;
        const isinvalidCheckoutRequest =
            isCheckoutLoginPath && !isInitiatedByCheckout();

        isinvalidCheckoutRequest && this._store.dispatch(replace(BASE_URL));

        const routesParams = {
            env,
            footerLinks,
            request,
            user,
            featureFlags,
        };

        const routes = getRoutes(routesParams, this._store);

        return (
            <Provider store={this._store}>
                <Router history={this._history} routes={routes} />
            </Provider>
        );
    }
}
